/**
 * S5 운영자 API(role operator · FS-15 웹 콘솔 — 계약: 스크래치 s5-api.md '운영자'). 모두 감사 로그(서버).
 *   GET/POST /api/ops/runtime-config {config, baseVersion} · GET /api/ops/runtime-config/history
 *   GET /api/ops/modes · GET/PUT /api/ops/modes/{id}/versions/{v} {definition} · POST …/publish
 *   POST /api/ops/modes/validate {definition} → {ok, errors}
 *   GET /api/ops/catalog?type= · PUT /api/ops/catalog/{type}/{key}/versions/{v} {i18n:{ko,en}, payload} · POST …/publish · POST …/disable
 *   GET/PUT /api/ops/recommendations {items}
 *   GET /api/ops/assets · GET /api/ops/service-keys · POST …/{keyId}/rotate → {keyId(새 키), secret(한 번만), service, previousKeyId,
 *     previousExpiresAt(이전 키는 24시간 겹쳐 유효 — 폐기하면 즉시 끝)} · POST …/revoke · 운영자 전용(그 밖 403 HTTP-403)
 * P2-S4 룸 모니터(FS-15-01 · 계약 p2s4 §2.4):
 *   GET /api/ops/rooms/monitor → {tiles, rooms, alerts, pods} · POST /api/ops/room-servers/{podId}/drain {on} → 204
 *   GET /api/ops/rooms/{roomId} → 룸 상세 타임라인(감사·멤버 이벤트 · 가명 pid 만)
 * 검증 실패 422 는 {code, errors:[{path, message}]}(모드) 또는 {issues:[{path, reason}]}(런타임 설정 — S3 서버) 둘 다 받는다.
 */
import { useMemo } from "react";
import { ApiError } from "../../../lib/http/errors";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import type { Requester } from "../../../types/api";
import type { StageAnswers } from "../lib/stageTest";
import type {
  Issue,
  Json,
  JsonObject,
  ConfigVersion,
  ModeEntry,
  CatalogItem,
  RecommendationRow,
  AssetVersion,
  ServiceKey,
  Rotation,
  RoomMonitor,
  RoomDetail,
} from "../types";

/** 422 검증 실패 */
export class ValidationError extends Error {
  constructor(readonly issues: Issue[]) {
    super("validation");
    this.name = "ValidationError";
  }
}

/** 409 — 다른 운영자가 먼저 바꿈(baseVersion 불일치) 또는 배포된 버전 변경 */
export class Conflict extends Error {
  constructor() {
    super("conflict");
    this.name = "Conflict";
  }
}

export const CATALOG_TYPES = [
  "emote",
  "phrase",
  "sticker",
  "avatar",
  "env_prefab",
  "pico_reaction",
  "hint_rule",
  // 기구 무대 꾸밈(2026-10-08) — key = 무대 프리셋 키 · payload = 렌더러 StageDecorData(무대 꾸밈 탭이 편집)
  "stage_scene",
  // 운영자가 올린 무대 3D 모델(2026-10-08) — key = 모델 키 · payload = {glb: sha256, height}(무대 꾸밈 탭이 올린다)
  "stage_model",
  // 마이크로비트 장면 별 둘 · 셋 기준(2026-10-08) — key = 장면 키 · payload = {count|time|hold, labels}(무대 목표 탭이 편집)
  "mb_goal",
] as const;

export interface OpsApi {
  /** FS-15-01 룸 모니터 */
  roomMonitor(): Promise<RoomMonitor>;
  drainPod(podId: string, on: boolean): Promise<void>;
  roomDetail(roomId: string): Promise<RoomDetail>;
  runtimeConfig(): Promise<ConfigVersion | null>;
  saveRuntimeConfig(config: JsonObject, baseVersion: number | null): Promise<ConfigVersion>;
  runtimeHistory(): Promise<ConfigVersion[]>;
  modes(): Promise<ModeEntry[]>;
  modeDefinition(modeId: string, version: number): Promise<Json>;
  saveMode(modeId: string, version: number, definition: Json): Promise<void>;
  publishMode(modeId: string, version: number): Promise<void>;
  validateMode(definition: Json): Promise<{ ok: boolean; errors: Issue[] }>;
  catalog(type?: string): Promise<CatalogItem[]>;
  saveCatalog(
    type: string,
    key: string,
    version: number,
    body: { i18n: { ko: string; en: string }; payload: Json },
  ): Promise<void>;
  publishCatalog(type: string, key: string, version: number): Promise<void>;
  disableCatalog(type: string, key: string): Promise<void>;
  /** 무대 꾸밈 3D 모델 GLB 올리기(내용 주소 sha256 · 검사 실패 422 → ValidationError) */
  uploadStageModel(
    bytes: Uint8Array,
  ): Promise<{ sha256: string; bytes: number; triangles: number }>;
  /** 무대 정답(기구 무대 차시 Bench 정답 · 자산 서버 주소) — 무대 목표 편집기의 정답 시험 */
  stageAnswers(): Promise<StageAnswers>;
  /** 올린 GLB 받기(미리보기 — blob URL 로 렌더러에) */
  stageModelBytes(sha256: string): Promise<Uint8Array>;
  recommendations(): Promise<RecommendationRow[]>;
  saveRecommendations(items: RecommendationRow[]): Promise<RecommendationRow[]>;
  assets(): Promise<AssetVersion[]>;
  serviceKeys(): Promise<ServiceKey[]>;
  rotateKey(keyId: string): Promise<Rotation>;
  revokeKey(keyId: string): Promise<void>;
}

const listOf = <T>(v: unknown, ...keys: string[]): T[] => {
  if (Array.isArray(v)) return v as T[];
  const o = (v ?? {}) as Record<string, unknown>;
  for (const k of keys.length ? keys : ["items"]) if (Array.isArray(o[k])) return o[k] as T[];
  return [];
};
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const strs = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
const isObj = (v: unknown): v is JsonObject => !!v && typeof v === "object" && !Array.isArray(v);

export function readIssues(body: unknown): Issue[] {
  const b = (body ?? {}) as Record<string, unknown>;
  return listOf<Record<string, unknown>>(b, "errors", "issues").map((i) => ({
    path: str(i.path) ?? "/",
    message: str(i.message) ?? str(i.reason) ?? "invalid",
  }));
}

function translate(e: unknown): never {
  if (e instanceof ApiError) {
    if (e.status === 422) throw new ValidationError(readIssues(e.body));
    if (e.status === 409) throw new Conflict();
  }
  throw e;
}

function readConfig(v: unknown): ConfigVersion | null {
  const o = (v ?? {}) as Record<string, unknown>;
  const cur = isObj(o.current) ? (o.current as Record<string, unknown>) : o;
  const version = Number(cur.version);
  if (!Number.isFinite(version) || !isObj(cur.config)) return null;
  return {
    version,
    config: cur.config,
    changedKeys: strs(cur.changedKeys ?? cur.changed_keys),
    changedBy: str(cur.changedBy ?? cur.changed_by),
    createdAt: str(cur.createdAt ?? cur.created_at),
  };
}

function readCatalogItem(r: Record<string, unknown>): CatalogItem {
  const i = (r.i18n ?? {}) as Record<string, unknown>;
  return {
    type: str(r.type) ?? "",
    key: str(r.key) ?? "",
    version: Number(r.version) || 1,
    i18n: { ko: str(i.ko) ?? "", en: str(i.en) ?? "" },
    payload: (r.payload ?? null) as Json,
    enabled: r.enabled === true,
    publishedAt: str(r.publishedAt),
  };
}

const numOr = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;
const objOf = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
/** P2-S1 가짜·옛 응답의 로비("gallery")도 "lobby" 로 */
const modeOf = (v: unknown) => {
  const m = str(v);
  return m === "gallery" ? "lobby" : m;
};

export function readMonitor(v: unknown): RoomMonitor {
  const o = objOf(v);
  const t = objOf(o.tiles);
  const d = objOf(t.degrade);
  return {
    tiles: {
      tickP95Ms: numOr(t.tickP95Ms),
      downKbps: numOr(t.downKbps),
      reconnectRate: numOr(t.reconnectRate),
      fe303Rate: numOr(t.fe303Rate),
      degrade: {
        s1: numOr(d.s1) ?? 0,
        s2: numOr(d.s2) ?? 0,
        s3: numOr(d.s3) ?? 0,
        s4: numOr(d.s4) ?? 0,
      },
      modeCompletion: numOr(t.modeCompletion),
      activeRooms: numOr(t.activeRooms) ?? 0,
      joinP95S: numOr(t.joinP95S),
    },
    rooms: listOf<unknown>(o.rooms).map((x) => {
      const r = objOf(x);
      const id = str(r.roomId) ?? "";
      return {
        roomId: id,
        short: str(r.short) ?? id.slice(0, 4),
        course: str(r.course) ?? "—",
        modeId: modeOf(r.modeId) ?? "",
        members: numOr(r.members) ?? 0,
        capacity: numOr(r.capacity) ?? 30,
        tickP95Ms: numOr(r.tickP95Ms),
        degrade: numOr(r.degrade) ?? 0,
        podId: str(r.podId),
        state: str(r.state) ?? "open",
      };
    }),
    alerts: listOf<unknown>(o.alerts).map((x) => {
      const a = objOf(x);
      return {
        roomId: str(a.roomId),
        short: str(a.short),
        kind: str(a.kind) ?? "",
        detail: str(a.detail) ?? "",
      };
    }),
    pods: listOf<unknown>(o.pods).map((x) => {
      const p = objOf(x);
      return {
        podId: str(p.podId) ?? "",
        rooms: numOr(p.rooms) ?? 0,
        capacity: numOr(p.capacity) ?? 4,
        draining: p.draining === true,
        alive: p.alive !== false,
        lastSeenS: numOr(p.lastSeenS),
      };
    }),
  };
}

export function readRoomDetail(v: unknown, roomId: string): RoomDetail {
  const top = objOf(v);
  // 제어 평면은 {room:{…}, timeline:[…]} — 룸 필드는 room 안, 타임라인은 바깥
  const o = { ...top, ...objOf(top.room) };
  return {
    roomId: str(o.roomId) ?? roomId,
    short: str(o.short),
    course: str(o.course),
    modeId: modeOf(o.modeId),
    state: str(o.state),
    podId: str(o.podId),
    openedAt: str(o.openedAt),
    events: listOf<unknown>(o, "events", "timeline", "items").map((x) => {
      const e = objOf(x);
      const detail = e.detail ?? e.payload;
      return {
        at: str(e.at) ?? str(e.createdAt),
        kind: str(e.kind) ?? str(e.action) ?? str(e.type) ?? "",
        pid: str(e.pid) ?? str(e.actorPid) ?? str(e.actor),
        detail: typeof detail === "string" ? detail : detail == null ? "" : JSON.stringify(detail),
      };
    }),
  };
}

export function createOpsApi(request: Requester): OpsApi {
  const o = "/api/ops";
  const send = (method: string, body?: unknown): RequestInit =>
    body === undefined ? { method } : { method, body: JSON.stringify(body) };
  const seg = encodeURIComponent;
  const guard = async <T>(p: Promise<T>): Promise<T> => {
    try {
      return await p;
    } catch (e) {
      return translate(e);
    }
  };
  return {
    roomMonitor: async () => readMonitor(await request<unknown>(`${o}/rooms/monitor`)),
    async drainPod(podId, on) {
      await request(`${o}/room-servers/${seg(podId)}/drain`, send("POST", { on }));
    },
    roomDetail: async (roomId) =>
      readRoomDetail(await request<unknown>(`${o}/rooms/${seg(roomId)}`), roomId),
    runtimeConfig: async () => readConfig(await request<unknown>(`${o}/runtime-config`)),
    async saveRuntimeConfig(config, baseVersion) {
      const v = await guard(
        request<unknown>(`${o}/runtime-config`, send("POST", { config, baseVersion })),
      );
      const c = readConfig(v);
      if (!c) throw new ApiError("NET-SERVER", 200, "runtime-config 응답 형식");
      return c;
    },
    async runtimeHistory() {
      const v = await request<unknown>(`${o}/runtime-config/history`);
      return listOf<unknown>(v, "items", "history")
        .map(readConfig)
        .filter((x): x is ConfigVersion => !!x);
    },
    async modes() {
      const v = await request<unknown>(`${o}/modes`);
      return listOf<Record<string, unknown>>(v, "items").map((m) => ({
        modeId: str(m.modeId) ?? "",
        versions: listOf<Record<string, unknown>>(m.versions)
          .map((x) => ({
            version: Number(x.version) || 1,
            enabled: x.enabled === true,
            publishedAt: str(x.publishedAt),
            publishedBy: str(x.publishedBy),
            principleTags: strs(x.principleTags),
            phase: str(x.phase) ?? "PH1",
          }))
          .sort((a, b) => a.version - b.version),
      }));
    },
    async modeDefinition(id, v) {
      const r = await request<{ definition?: Json }>(`${o}/modes/${seg(id)}/versions/${v}`);
      return (r?.definition ?? r) as Json;
    },
    async saveMode(id, v, definition) {
      await guard(request(`${o}/modes/${seg(id)}/versions/${v}`, send("PUT", { definition })));
    },
    async publishMode(id, v) {
      await guard(request(`${o}/modes/${seg(id)}/versions/${v}/publish`, send("POST")));
    },
    async validateMode(definition) {
      try {
        const r = await request<{ ok?: unknown; errors?: unknown }>(
          `${o}/modes/validate`,
          send("POST", { definition }),
        );
        return { ok: r?.ok === true, errors: readIssues(r) };
      } catch (e) {
        if (e instanceof ApiError && e.status === 422)
          return { ok: false, errors: readIssues(e.body) };
        throw e;
      }
    },
    async catalog(type) {
      const q = type ? `?type=${seg(type)}` : "";
      return listOf<Record<string, unknown>>(await request<unknown>(`${o}/catalog${q}`)).map(
        readCatalogItem,
      );
    },
    async saveCatalog(type, key, v, body) {
      await guard(
        request(`${o}/catalog/${seg(type)}/${seg(key)}/versions/${v}`, send("PUT", body)),
      );
    },
    async publishCatalog(type, key, v) {
      await guard(
        request(`${o}/catalog/${seg(type)}/${seg(key)}/versions/${v}/publish`, send("POST")),
      );
    },
    async uploadStageModel(bytes) {
      const r = await guard(
        request<Record<string, unknown>>(`${o}/stage-models`, {
          method: "POST",
          body: bytes as BodyInit,
          headers: { "Content-Type": "application/octet-stream" },
        }),
      );
      return {
        sha256: str(r?.sha256) ?? "",
        bytes: Number(r?.bytes) || 0,
        triangles: Number(r?.triangles) || 0,
      };
    },
    async stageAnswers() {
      const r = (await request<Record<string, unknown>>(`${o}/stage-answers`)) ?? {};
      const lessons = (isObj(r.lessons) ? r.lessons : {}) as unknown as StageAnswers["lessons"];
      const base = str(r.assetBase) ?? "";
      return { lessons, assetBase: base.endsWith("/") ? base : `${base}/` };
    },
    stageModelBytes: (sha) =>
      request<Uint8Array>(`/api/stage-models/${seg(sha)}`, { as: "bytes" } as RequestInit),
    async disableCatalog(type, key) {
      await guard(request(`${o}/catalog/${seg(type)}/${seg(key)}/disable`, send("POST")));
    },
    async recommendations() {
      return listOf<Record<string, unknown>>(await request<unknown>(`${o}/recommendations`)).map(
        (r) => ({
          principleTag: str(r.principleTag) ?? "",
          modeId: str(r.modeId) ?? "",
          presetKey: str(r.presetKey),
          weight: typeof r.weight === "number" ? r.weight : Number(r.weight) || 0,
          source: r.source === "worksheet" ? "worksheet" : "manual",
        }),
      );
    },
    async saveRecommendations(items) {
      const v = await guard(request<unknown>(`${o}/recommendations`, send("PUT", { items })));
      const out = listOf<RecommendationRow>(v);
      return out.length ? out : items;
    },
    async assets() {
      return listOf<Record<string, unknown>>(await request<unknown>(`${o}/assets`)).map((a) => ({
        library: str(a.library) ?? "",
        key: str(a.key) ?? "*",
        version: str(a.version) ?? String(a.version ?? ""),
        contentHash: str(a.contentHash) ?? "",
        status: str(a.status) ?? "",
        createdAt: str(a.createdAt),
      }));
    },
    async serviceKeys() {
      return listOf<Record<string, unknown>>(await request<unknown>(`${o}/service-keys`)).map(
        (k) => ({
          keyId: str(k.keyId) ?? "",
          service: str(k.service) ?? "",
          status: str(k.status) ?? "",
          createdAt: str(k.createdAt),
          rotatedAt: str(k.rotatedAt),
          expiresAt: str(k.expiresAt),
          successorKeyId: str(k.successorKeyId),
        }),
      );
    },
    async rotateKey(keyId) {
      const r = await guard(
        request<Record<string, unknown>>(`${o}/service-keys/${seg(keyId)}/rotate`, send("POST")),
      );
      return {
        keyId: str(r?.keyId) ?? keyId,
        secret: str(r?.secret) ?? "",
        service: str(r?.service),
        previousKeyId: str(r?.previousKeyId),
        previousExpiresAt: str(r?.previousExpiresAt),
      };
    },
    async revokeKey(keyId) {
      await guard(request(`${o}/service-keys/${seg(keyId)}/revoke`, send("POST")));
    },
  };
}

export function useOpsApi(): OpsApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const ok = state.status === "ready" && state.session.role === "operator";
  return useMemo(() => (ok ? createOpsApi(manager.request) : null), [manager, ok]);
}

/** 중첩 설정 → 점 표기 잎(배열·원시값은 잎). 서버 changedKeys 와 같은 규칙 */
export function flattenConfig(c: JsonObject, prefix = "", out: Record<string, Json> = {}) {
  for (const [k, v] of Object.entries(c)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (isObj(v) && Object.keys(v).length > 0) flattenConfig(v, key, out);
    else out[key] = v;
  }
  return out;
}

/** 점 표기 잎 → 중첩 설정 */
export function unflattenConfig(flat: Record<string, Json>): JsonObject {
  const out: JsonObject = {};
  for (const [path, v] of Object.entries(flat)) {
    const parts = path.split(".");
    let cur = out;
    parts.forEach((p, i) => {
      if (i === parts.length - 1) cur[p] = v;
      else {
        if (!isObj(cur[p])) cur[p] = {};
        cur = cur[p] as JsonObject;
      }
    });
  }
  return out;
}
